import * as Bixby from '$/core';

export interface FileValidationRequest {
  id: string;
  fileName: string;
  title: string;
  description: string;
  companyDocumentCategory_Ids: string[];
  clientDataSiteProvider_Id?: string;
  rowNumber?: number;
  index?: number;
  overrideAlreadyExistsError?: boolean;
}

export interface CompanyDocumentFilePrevalidationRequest {
  clientDataSiteProvider_Id?: string;
}

export interface CompanyDocumentZipFilePrevalidationRequest {
  clientDataSiteProvider_Id?: string;
}

export abstract class DocumentsAdminService {
  private static baseUrl = `${Bixby.Constants.API_ADMIN_PREFIX}/${Bixby.Constants.API_DOCUMENTSCONTROLLER}`;

  public static async deleteDocument(id: string): Promise<void> {
    const url = `${DocumentsAdminService.baseUrl}/${id}`;
    const route = Bixby.API.ServerRoute.forAction(url);
    return await Bixby.API.delete(route);
  }

  public static async deleteDocumentFromBlobStorage(id: string): Promise<void> {
    const url = `${DocumentsAdminService.baseUrl}/${id}/${Bixby.Constants.API_ACTION_DELETED}`;
    const route = Bixby.API.ServerRoute.forAction(url);
    return await Bixby.API.delete(route);
  }

  public static async deleteDocumentFromDatabase(id: string): Promise<void> {
    const url = `${DocumentsAdminService.baseUrl}/${id}/${Bixby.Constants.API_ACTION_ERROR}`;
    const route = Bixby.API.ServerRoute.forAction(url);
    return await Bixby.API.delete(route);
  }

  public static async deleteAllPublishedDocuments(companyId: string): Promise<void> {
    const url = `${DocumentsAdminService.baseUrl}/company/${companyId}/${Bixby.Constants.API_ACTION_PUBLISHED}`;
    const route = Bixby.API.ServerRoute.forAction(url);
    return await Bixby.API.delete(route);
  }

  public static async deleteAllPendingDocuments(companyId: string): Promise<void> {
    const url = `${DocumentsAdminService.baseUrl}/company/${companyId}/${Bixby.Constants.API_ACTION_PENDING_REQUIRES_APPROVAL}`;
    const route = Bixby.API.ServerRoute.forAction(url);
    return await Bixby.API.delete(route);
  }

  public static async deleteAllDeletedDocumentsFromBlobStorage(companyId: string): Promise<void> {
    const url = `${DocumentsAdminService.baseUrl}/company/${companyId}/${Bixby.Constants.API_ACTION_DELETED}`;
    const route = Bixby.API.ServerRoute.forAction(url);
    return await Bixby.API.delete(route);
  }

  public static async deleteAllErrorDocuments(companyId: string): Promise<void> {
    const url = `${DocumentsAdminService.baseUrl}/company/${companyId}/${Bixby.Constants.API_ACTION_ERROR}`;
    const route = Bixby.API.ServerRoute.forAction(url);
    return await Bixby.API.delete(route);
  }

  public static async deleteSelectedDocumentsFromBlobStorage(ids: string[]) {
    const url = `${DocumentsAdminService.baseUrl}/${Bixby.Constants.API_ACTION_DELETED}`;
    const route = Bixby.API.ServerRoute.forAction(url);
    return await Bixby.API.put(route, ids);
  }
  
  public static async deleteSelectedDocumentsFromDb(ids: string[]) {
    const url = `${DocumentsAdminService.baseUrl}/${Bixby.Constants.API_ACTION_DELETE_FROM_DB}`;
    const route = Bixby.API.ServerRoute.forAction(url);
    return await Bixby.API.put(route, ids);
  }

  public static async prevalidateFiles(
    companyId: string,
    files: any[],
    clientDataSiteProvider_Id?: string
  ): Promise<Bixby.Models.CompanyDocumentPrevalidationResult[]> {
    const url = `${DocumentsAdminService.baseUrl}/${Bixby.Constants.API_ACTION_COMPANY}/${companyId}/${Bixby.Constants.API_ACTION_PREVALIDATE_FILES}`;
    const route = Bixby.API.ServerRoute.forAction(url);
    const request: CompanyDocumentFilePrevalidationRequest = {
      clientDataSiteProvider_Id: clientDataSiteProvider_Id,
    };

    const formData = new FormData();
    files.forEach(_ => {
      formData.append('files', _, _.name);
    });

    formData.append('request', JSON.stringify(request));

    return await Bixby.API.postLargeJSON(route, formData);
  }

  public static async manualPreValidateFiles(
    companyId: string,
    files: any[],
    clientDataSiteProvider_Id?: string
  ): Promise<Bixby.Models.CompanyDocumentPrevalidationResult[]> {
    const url = `${DocumentsAdminService.baseUrl}/${Bixby.Constants.API_ACTION_COMPANY}/${companyId}/prevalidate-files-manual`;
    const route = Bixby.API.ServerRoute.forAction(url);
    const request: CompanyDocumentFilePrevalidationRequest = {
      clientDataSiteProvider_Id: clientDataSiteProvider_Id,
    };

    const formData = new FormData();
    files.forEach(_ => {
      formData.append('files', _, _.name);
    });

    formData.append('request', JSON.stringify(request));

    return await Bixby.API.postLargeJSON(route, formData);
  }

  public static async prevalidateZip(
    companyId: string,
    zipFile: File,
    clientDataSiteProvider_Id?: string
  ): Promise<Bixby.Models.CompanyDocumentPrevalidationResult[]> {
    const url = `${DocumentsAdminService.baseUrl}/${Bixby.Constants.API_ACTION_COMPANY}/${companyId}/${Bixby.Constants.API_ACTION_PREVALIDATE_ZIP}`;
    const route = Bixby.API.ServerRoute.forAction(url);
    const request: CompanyDocumentZipFilePrevalidationRequest = {
      clientDataSiteProvider_Id: clientDataSiteProvider_Id,
    };

    const formData = new FormData();
    formData.set('zipFile', zipFile);
    formData.append('request', JSON.stringify(request));

    return await Bixby.API.postJSON(route, formData);
  }

  public static async manualPrevalidateZip(
    companyId: string,
    zipFile: File,
    clientDataSiteProvider_Id?: string
  ): Promise<Bixby.Models.CompanyDocumentPrevalidationResult[]> {
    const url = `${DocumentsAdminService.baseUrl}/${Bixby.Constants.API_ACTION_COMPANY}/${companyId}/prevalidate-zip-manual`;
    const route = Bixby.API.ServerRoute.forAction(url);
    const request: CompanyDocumentZipFilePrevalidationRequest = {
      clientDataSiteProvider_Id: clientDataSiteProvider_Id,
    };

    const formData = new FormData();
    formData.set('zipFile', zipFile);
    formData.append('request', JSON.stringify(request));

    return await Bixby.API.postJSON(route, formData);
  }

  public static async validateDocuments(
    companyId: string,
    documents: FileValidationRequest[]
  ): Promise<Bixby.Models.CompanyDocumentValidationResult[]> {
    const url = `${DocumentsAdminService.baseUrl}/${Bixby.Constants.API_ACTION_COMPANY}/${companyId}/${Bixby.Constants.API_ACTION_VALIDATE}`;
    const route = Bixby.API.ServerRoute.forAction(url);
    return await Bixby.API.post(route, documents);
  }

  public static async submitBulkDocuments(companyId: string, documents: Bixby.Models.CompanyDocumentSubmission[], files: File[]) {
    const url = `${DocumentsAdminService.baseUrl}/${Bixby.Constants.API_ACTION_COMPANY}/${companyId}/${Bixby.Constants.API_ACTION_SUBMIT_BULK}`;
    const route = Bixby.API.ServerRoute.forAction(url);

    const formData = new FormData();
    files.forEach(_ => {
      formData.append('files', _, _.name);
    });

    formData.append('request', JSON.stringify(documents));

    return await Bixby.API.postLargeJSON(route, formData);
  }

  public static async sharedSubmitBulkDocuments(companyId: string, documents: Bixby.Models.CompanyDocumentSubmission[]) {
    const url = `${DocumentsAdminService.baseUrl}/${Bixby.Constants.API_ACTION_COMPANY}/${companyId}/submit-bulk-shared`;
    const route = Bixby.API.ServerRoute.forAction(url);

    const formData = new FormData();
    formData.append('request', JSON.stringify(documents));

    return await Bixby.API.postLargeJSON(route, formData);
  }

  public static async submitZipFile(
    companyId: string,
    zipfile: File,
    documents: Bixby.Models.CompanyDocumentSubmission[]
  ) {
    const url = `${DocumentsAdminService.baseUrl}/${Bixby.Constants.API_ACTION_COMPANY}/${companyId}/${Bixby.Constants.API_ACTION_SUBMIT_ZIP}`;
    const route = Bixby.API.ServerRoute.forAction(url);

    const formData = new FormData();
    formData.set('request', JSON.stringify(documents));
    formData.append('zipfile', zipfile);

    return await Bixby.API.postLargeJSON(route, formData);
  }

  public static async manualSubmitZipFile(
    companyId: string,
    documents: Bixby.Models.CompanyDocumentSubmission[]
  ) {
    const url = `${DocumentsAdminService.baseUrl}/${Bixby.Constants.API_ACTION_COMPANY}/${companyId}/submit-zip-manual`;
    const route = Bixby.API.ServerRoute.forAction(url);

    const formData = new FormData();
    formData.set('request', JSON.stringify(documents));

    return await Bixby.API.postLargeJSON(route, formData);
  }

  public static async updateDocument(id: string, editModalData: any) {
    const url = `${DocumentsAdminService.baseUrl}/${id}`;
    const route = Bixby.API.ServerRoute.forAction(url);

    return await Bixby.API.put(route, editModalData);
  }

  public static async BulkUpdateDocumentTag(editModalData: any) {
    const url = `${DocumentsAdminService.baseUrl}/update`;
    const route = Bixby.API.ServerRoute.forAction(url);

    return await Bixby.API.put(route, editModalData);
  }

  public static async downloadFromZipFile(
    zipFile: File,
    fileName: string,
    index?: number
  ): Promise<Bixby.Models.FileResultMetadata> {
    const url = `${DocumentsAdminService.baseUrl}/${Bixby.Constants.API_ACTION_DOWNLOAD_FROM_ZIP}`;
    const route = Bixby.API.ServerRoute.forAction(url);

    const formData = new FormData();
    formData.set('request', JSON.stringify({ fileName, index }));
    formData.append('zipFile', zipFile);

    return await Bixby.API.postFileWithMetadata(route, formData);
  }

  public static async downloadAllWatermarkRemovedFilesAsZip(companyId: string, request: Bixby.Models.CompanyDocumentWatermarkRemovedFiles): Promise<Bixby.Models.FileResultMetadata> {
    const url = `${DocumentsAdminService.baseUrl}/${Bixby.Constants.API_ACTION_DOWNLOAD_WATERMARK_REMOVED_FILES}/${companyId}`;

    const formData = new FormData();
    formData.set('request', JSON.stringify(request));
    const route = Bixby.API.ServerRoute.forAction(url);
    return await Bixby.API.postFileWithMetadata(route, formData);
  }

  public static async downloadFilteredDocumentFiles(companyId: string, request: Bixby.Models.CompanyDocumentFilesDownload): Promise<Bixby.Models.FileResultMetadata> {
    const url = `${DocumentsAdminService.baseUrl}/download-filtered-files/${companyId}`;

    const formData = new FormData();
    formData.set('request', JSON.stringify(request));
    const route = Bixby.API.ServerRoute.forAction(url);
    return await Bixby.API.postFileWithMetadata(route, formData);
  }

  public static async approveDocuments(ids: string[]) {
    const url = `${DocumentsAdminService.baseUrl}/${Bixby.Constants.API_ACTION_APPROVE}`;
    const route = Bixby.API.ServerRoute.forAction(url);
    return await Bixby.API.post(route, ids);
  }

  public static async getDocumentsByCompanyId(companyId: string): Promise<Bixby.Models.CompanyDocument[]> {
    const url = `${DocumentsAdminService.baseUrl}/${Bixby.Constants.API_ACTION_COMPANY}/${companyId}`;
    const route = Bixby.API.ServerRoute.forAction(url);
    return await Bixby.API.get(route);
  }

  public static async getPendingApprovalSummary(): Promise<Bixby.Models.CompanyDocPendingApprovalSummary[]> {
    const url = `${DocumentsAdminService.baseUrl}/${Bixby.Constants.API_ACTION_PENDING_APPROVAL_SUMMARY}`;
    const route = Bixby.API.ServerRoute.forAction(url);
    return await Bixby.API.get(route);
  }

  public static async restoreDocument(id: string) {
    const url = `${DocumentsAdminService.baseUrl}/${Bixby.Constants.API_ACTION_RESTORE}/${id}`;
    const route = Bixby.API.ServerRoute.forAction(url);
    return await Bixby.API.post(route, id);
  }

  public static async getSuggetedTagsByCompanyId(companyId: string): Promise<Bixby.Models.CompanyDocumentSuggestedTag[]> {
    const url = `${DocumentsAdminService.baseUrl}/${Bixby.Constants.API_ACTION_SUGGESTED_TAGS}/${companyId}`;
    const route = Bixby.API.ServerRoute.forAction(url);
    return await Bixby.API.get(route, companyId);
  }

  public static async submitSuggestedTags(CompanyDocumentSuggestedTag: Bixby.Models.CompanyDocumentSuggestedTag[]) {
    const url = `${DocumentsAdminService.baseUrl}/${Bixby.Constants.API_ACTION_SUBMIT_SUGGESTED_TAGS}`;
    const route = Bixby.API.ServerRoute.forAction(url);
    return await Bixby.API.post(route, CompanyDocumentSuggestedTag);
  }

 public static async removeWatermark(CompanyDocumentremoveWatermark: Bixby.Models.CompanyDocumentRemoveWatermark): Promise<Bixby.Models.CompanyDocumentRemoveWatermarkResponse> {
    const url = `${DocumentsAdminService.baseUrl}/${Bixby.Constants.API_ACTION_REMOVE_WATERMARK}`;
    const route = Bixby.API.ServerRoute.forAction(url);
    return await Bixby.API.post(route, CompanyDocumentremoveWatermark);
  }

  public static async sharedRemoveWatermark(CompanyDocumentremoveWatermark: Bixby.Models.CompanyDocumentRemoveWatermark): Promise<Bixby.Models.CompanyDocumentRemoveWatermarkResponse> {
    const url = `${DocumentsAdminService.baseUrl}/remove-watermark-shared`;
    const route = Bixby.API.ServerRoute.forAction(url);
    return await Bixby.API.post(route, CompanyDocumentremoveWatermark);
  }
}
